import React, { useEffect, lazy } from 'react';
import { Button } from '../components/ui/Button';
import { ArrowRight, ShieldCheck, Lock } from 'lucide-react';
import { useSpotCountdown } from '../hooks/useSpotCountdown';
import { useMetaPixel } from '../hooks/useMetaPixel';
import { useDeadlineCountdown } from '../hooks/useDeadlineCountdown';
import { FloatingWhatsApp } from '../components/FloatingWhatsApp';
import { FloatingCta } from '../components/FloatingCta';
export function CheckoutPage() {
  const {
    spots
  } = useSpotCountdown();
  const {
    trackCustomEvent
  } = useMetaPixel();
  const countdown = useDeadlineCountdown();
  const ACTIVE_CHECKOUT_URL = 'https://www.fanbasis.com/agency-checkout/true-marketer/KA5Pz';
  useEffect(() => {
    trackCustomEvent('Checkout-Page-Vist');
  }, [trackCustomEvent]);
  const handleCheckout = () => {
    trackCustomEvent('Initiate-Checkout-97');
    window.location.href = ACTIVE_CHECKOUT_URL;
  };
  const pad = (n: number) => String(n).padStart(2, '0');
  return <div className="min-h-screen w-full flex flex-col items-center bg-[#FAFAFA] px-4 py-6 sm:py-10" data-id="element-28">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden" data-id="element-29">
        <div className="p-5 sm:p-8 space-y-5" data-id="element-30">
          {/* Header — compact, above the fold */}
          <div className="text-center space-y-1.5" data-id="element-31">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900" data-id="element-32">
              AI WEB CLOSER
            </h1>
            <p className="text-gray-500 text-sm sm:text-base" data-id="element-33">
              Acceso Completo al Programa
            </p>
          </div>

          {/* Spots urgency pill */}
          {!countdown.isExpired && <div className={`text-center text-xs sm:text-sm font-bold uppercase tracking-wider py-2 px-3 rounded-lg transition-colors duration-300 ${spots === 1 ? 'text-red-700 bg-red-50 border border-red-200 animate-flash' : 'text-emerald-700 bg-emerald-50 border border-emerald-200'}`} data-id="element-34">
              🔥 Solo {spots}{' '}
              {spots === 1 ? 'Cupo Disponible' : 'Cupos Disponibles'}
            </div>}

          {/* Price block */}
          <div className="text-center py-1" data-id="element-35">
            <div className="text-[11px] sm:text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1" data-id="element-36">
              {countdown.isExpired ? 'Precio Regular' : 'Precio Especial Hoy'}
            </div>
            <div className="flex items-baseline justify-center gap-3" data-id="element-37">
              <span className="text-5xl sm:text-6xl font-bold text-gray-900 tracking-tight tabular-nums" data-id="element-38">
                {countdown.isExpired ? '$1997' : '$297'}
              </span>
              {!countdown.isExpired && <span className="text-lg sm:text-xl text-gray-400 line-through tabular-nums" data-id="element-39">
                  $1997
                </span>}
            </div>
          </div>

          {/* PRIMARY CTA — above the fold on mobile */}
          {!countdown.isExpired ? <div className="space-y-3" data-id="element-40">
              <Button onClick={handleCheckout} size="lg" className="w-full text-base sm:text-lg font-bold h-16 sm:h-[68px] shadow-lg shadow-emerald-600/20 ring-1 ring-emerald-500/30" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-42" />} data-id="element-41">
                Comprar Ahora
              </Button>
              <div className="flex items-center justify-center gap-4 text-[11px] sm:text-xs text-gray-500" data-id="element-43">
                <span className="flex items-center gap-1" data-id="element-44">
                  <Lock className="w-3 h-3" data-id="element-45" />
                  Pago seguro
                </span>
                <span className="flex items-center gap-1" data-id="element-46">
                  <ShieldCheck className="w-3 h-3" data-id="element-47" />
                  Garantía 30 días
                </span>
              </div>
            </div> : <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 space-y-2 text-center" data-id="element-48">
              <p className="text-base font-semibold text-gray-900" data-id="element-49">
                La oferta especial ha terminado
              </p>
              <p className="text-sm text-gray-600" data-id="element-50">
                Contáctanos por WhatsApp para conocer las opciones disponibles.
              </p>
            </div>}

          {/* Countdown — urgency reinforcer below CTA */}
          {!countdown.isExpired && <div className="bg-red-600 rounded-xl p-3.5 space-y-2" data-id="element-51">
              <p className="text-[10px] sm:text-xs uppercase tracking-wider text-red-100 font-semibold text-center" data-id="element-52">
                ⏳ El precio sube a $1997 el viernes en
              </p>
              <div className="flex justify-center items-baseline gap-1.5 sm:gap-3" data-id="element-53">
                {countdown.days > 0 && <>
                    <div className="flex flex-col items-center min-w-0 flex-1 max-w-[60px]" data-id="element-54">
                      <span className="text-xl sm:text-2xl font-bold text-white tabular-nums" data-id="element-55">
                        {pad(countdown.days)}
                      </span>
                      <span className="text-[9px] sm:text-[10px] uppercase text-red-200" data-id="element-56">
                        Días
                      </span>
                    </div>
                    <span className="text-xl sm:text-2xl font-bold text-red-300" data-id="element-57">
                      :
                    </span>
                  </>}
                <div className="flex flex-col items-center min-w-0 flex-1 max-w-[60px]" data-id="element-58">
                  <span className="text-xl sm:text-2xl font-bold text-white tabular-nums" data-id="element-59">
                    {pad(countdown.hours)}
                  </span>
                  <span className="text-[9px] sm:text-[10px] uppercase text-red-200" data-id="element-60">
                    Hrs
                  </span>
                </div>
                <span className="text-xl sm:text-2xl font-bold text-red-300" data-id="element-61">
                  :
                </span>
                <div className="flex flex-col items-center min-w-0 flex-1 max-w-[60px]" data-id="element-62">
                  <span className="text-xl sm:text-2xl font-bold text-white tabular-nums" data-id="element-63">
                    {pad(countdown.minutes)}
                  </span>
                  <span className="text-[9px] sm:text-[10px] uppercase text-red-200" data-id="element-64">
                    Min
                  </span>
                </div>
                <span className="text-xl sm:text-2xl font-bold text-red-300" data-id="element-65">
                  :
                </span>
                <div className="flex flex-col items-center min-w-0 flex-1 max-w-[60px]" data-id="element-66">
                  <span className="text-xl sm:text-2xl font-bold text-white tabular-nums" data-id="element-67">
                    {pad(countdown.seconds)}
                  </span>
                  <span className="text-[9px] sm:text-[10px] uppercase text-red-200" data-id="element-68">
                    Seg
                  </span>
                </div>
              </div>
            </div>}

          {/* Hero image — moved below CTA, lazy-loaded */}
          <img src="https://cdn.magicpatterns.com/uploads/g4SAjMpoXz3BM6AyA6VfjW/AI_WEB_CLOSER.png" alt="AI Web Closer" loading="lazy" decoding="async" className="w-full h-auto rounded-xl" data-id="element-69" />

          {/* Secondary CTA after image */}
          {!countdown.isExpired && <Button onClick={handleCheckout} size="lg" className="w-full text-base sm:text-lg font-semibold h-14 sm:h-16" rightIcon={<ArrowRight className="w-5 h-5" data-id="element-71" />} data-id="element-70">
              Comprar Ahora
            </Button>}

          <p className="text-[11px] sm:text-xs text-gray-400 text-center pt-1" data-id="element-72">
            Serás redirigido a nuestra página de pago segura.
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-6 mb-24 text-center text-gray-400 text-xs sm:text-sm px-4" data-id="element-73">
        &copy; {new Date().getFullYear()} AI Web Closer. Todos los derechos
        reservados.
      </div>

      {/* Sticky floating CTA — always-accessible buy button */}
      {!countdown.isExpired && <FloatingCta onClick={handleCheckout} price="$297" originalPrice="$1997" label="COMPRAR" showAfter={400} data-id="element-74" />}

      <FloatingWhatsApp link="https://wa.link/infocommerce" data-id="element-75" />
    </div>;
}